import { EnhancedSupplyCard, SupplyCallbackType, SupplyListLxConfig, SupplyType } from "@/types/supplyList";
import PoiCard, { PoiCardSkeleton } from "../poiCard";
import ProductCard from "../productCard";
import "./index.scss";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { useTracker } from "@/context/TrackerContext";
import { COUPON_SUPPLY_BID_MV } from "@/track/coupon-wall";

/**
 * 供给列表骨架屏
 */
const SupplyListSkeleton = () => {
  return (
    <div className="supply-list">
      <PoiCardSkeleton />
      <PoiCardSkeleton />
    </div>
  );
};

/**
 * 供给列表兜底展示
 */

// 兜底类型
enum FallbackType {
  Error = "Error", // 接口异常
  NoData = "NoData", // 没有数据
  NoDataInFilter = "NoDataInFilter", // 筛选无数据
  Loading = "Loading", // 加载中
}

// 兜底提示图片
const FallbackImgMap: Record<FallbackType, string> = {
  [FallbackType.Error]: "https://p0.meituan.net/pepperfe/27d3528f693c329c8d90e25622756b5854576.png",
  [FallbackType.NoData]:
    "https://p1.meituan.net/pepperfe/54f2534a60b0247f1cffcd9329ad03c839960.png",
  [FallbackType.NoDataInFilter]:
    "https://p1.meituan.net/pepperfe/54f2534a60b0247f1cffcd9329ad03c839960.png",
  [FallbackType.Loading]:
    "https://p0.meituan.net/pepperfe/65147789407706bd3a284a78c73a8cc7679965.gif",
};

// 兜底文案
const FallbackTextMap: Record<FallbackType, string> = {
  [FallbackType.Error]: "诶呀，系统出小差了，手动刷新试试吧~",
  [FallbackType.NoData]: "抱歉，您附近没有可下单的商家，\n手动切换城市试试吧～",
  [FallbackType.NoDataInFilter]: "抱歉，没有符合条件的商家\n您可以减少筛选项试试～",
  [FallbackType.Loading]: "优惠加载中，请稍后...",
};

const SupplyListFallback = (props: { type?: FallbackType; onReload: () => void }) => {
  const { type = FallbackType.NoData, onReload } = props;
  return (
    <div className="supply-list-fallback-container">
      <img className="fallback-img" src={FallbackImgMap[type]} alt="empty" />
      <div className="fallback-text">{FallbackTextMap[type]}</div>
      {type === FallbackType.Error && (
        <button className="fallback-btn" onClick={onReload}>
          刷新
        </button>
      )}
    </div>
  );
};

/**
 * 供给列表
 */
interface SupplyListProps {
  supplyList: EnhancedSupplyCard[];
  isInitFinished: boolean; // 初始化是否结束，用于展示骨架屏
  isLoading: boolean; // 是否加载中
  isLastPage: boolean; // 是否最后一页
  isRequestError: boolean; // 是否接口异常
  isCouponCallback: boolean; // 是否券召回
  isCouponPlatformStrategyRecall: boolean; // 是否开启了券平台召回最优券策略，用于上报下挂券相关埋点（这些埋点只用于券平台召回最优券场景）
  isFiltered: boolean; // 是否筛选
  enablePoiCouponCountdown: boolean; // 是否开启下挂券倒计时
  onLoadMore: () => void; // 加载下一页
  onReload: () => void; // 刷新
  // 埋点
  tabId?: number | null; // 当前选中的 tabId，用于埋点上报
  lxConfig: SupplyListLxConfig;
}

const SupplyList: React.FC<SupplyListProps> = function (props) {
  const {
    supplyList = [],
    isInitFinished,
    isLoading,
    isLastPage,
    isRequestError,
    isCouponCallback = false,
    isCouponPlatformStrategyRecall = false,
    isFiltered = false,
    enablePoiCouponCountdown = false,
    lxConfig,
    tabId, // 埋点上报
    onLoadMore,
    onReload,
  } = props || {};

  const tracker = useTracker();
  // 加载更多
  const loadMoreElementRef = useRef(null);

  // 检测触底加载更多
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreElementRef.current) {
      observer.observe(loadMoreElementRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [onLoadMore]);

  // 错误兜底类型
  const fallbackType = useMemo(() => {
    // 1. 接口异常
    if (isRequestError) {
      return FallbackType.Error;
    }
    // 2. 加载中
    if (isLoading) {
      return FallbackType.Loading;
    }
    // 3. 筛选、Tab 无数据
    if (supplyList.length === 0) {
      return isFiltered ? FallbackType.NoDataInFilter : FallbackType.NoData;
    }

    return null;
  }, [isRequestError, supplyList, isLoading, isFiltered]);

  // 开启了券平台召回最优券策略，上报供给列表曝光，页面最多只上报一次
  useEffect(() => {
    if (!isInitFinished || !tracker || !isCouponPlatformStrategyRecall) {
      return;
    }
    tracker.moduleView(COUPON_SUPPLY_BID_MV.SUPPLY_LIST);
  }, [tracker, isInitFinished, isCouponPlatformStrategyRecall])

  // 初始化未完成，展示骨架屏
  if (!isInitFinished) {
    return <SupplyListSkeleton />;
  }
  return (
    <div className="supply-list">
      {supplyList.length ? (
        // 有列表数据，正常展示
        <>
          {supplyList.map((item, index) =>
            <Fragment key={index}>
              {item.supplyType === SupplyType.Wayne ? (
                <PoiCard 
                  index={index + 1}
                  poiInfo={item.poiCard}
                  tabId={tabId}
                  supplyCallbackType={isCouponCallback ? SupplyCallbackType.CouponCallback : SupplyCallbackType.Default}
                  enableCountdown={enablePoiCouponCountdown}
                  lxConfig={lxConfig}
                  isCouponPlatformStrategyRecall={isCouponPlatformStrategyRecall}
                />
              ) : (
                <ProductCard index={index + 1} productInfo={item.productCard} />
              )}
            </Fragment>
          )}
          {/* 最后一页，不展示加载更多 */}
          {isLastPage && <div className="supply-list-bottom-tip">没有更多内容啦～</div>}
          {/* 上拉加载 Loading */}
          {!isLastPage && isLoading && (
            <div className="supply-list-bottom-tip">
              <img
                className="loading-img"
                src="https://p0.meituan.net/pepperfe/d8c1a02b683dd2e6e4beae6cdf7567671206.png"
              />
              加载中...
            </div>
          )}
          {/* 分页加载失败，展示重试按钮 */}
          {!isLastPage && !isLoading && isRequestError && (
            <div className="supply-list-bottom-tip">
              <button className="retry-btn" onClick={onLoadMore}>
                加载失败，点击重试
              </button>
            </div>
          )}
          {/* 加载分页占位符，该元素出现在视口中时触发加载更多 */}
          {!isLoading && !isLastPage && !isRequestError && (
            <div className="load-more-placeholder" ref={loadMoreElementRef} />
          )}
        </>
      ) : (
        // 无列表数据：展示兜底
        fallbackType && <SupplyListFallback type={fallbackType} onReload={onReload} />
      )}
    </div>
  );
};

export default SupplyList;
